import api from './api';

export interface Contact {
  type: string;
  value: string;
}

export interface Address {
  street: string;
  number: string;
  complement?: string;
  district: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  maritalStatus?: string;
  religion?: string;
  nationality?: string;
  contacts: Contact[];
  addresses: Address[];
  paymentMethod?: string;
  paymentPeriod?: string;
  consultationValue?: string;
  issuesInvoice?: boolean;
}

export interface PatientFormData {
  firstName: string;
  lastName: string;
  birthDate: string;
  maritalStatus: string;
  religion: string;
  nationality: string;
  email: string;
  phone: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  zipCode: string;
  paymentMethod: string;
  paymentPeriod: string;
  consultationValue: string;
  issuesInvoice: boolean;
}

const patientsService = {
  // Métodos CRUD
  async getAll(params?: { search?: string }): Promise<Patient[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    const url = `/person/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get<Patient[]>(url);
    return response.data || [];
  },

  async getById(id: string): Promise<Patient> {
    const response = await api.get<Patient>(`/person/${id}`);
    return response.data!;
  },

  async create(patient: Patient): Promise<Patient> {
    const response = await api.post<Patient>('/person/', patient);
    return response.data!;
  },

  async update(id: string, patient: Patient): Promise<Patient> {
    const response = await api.put<Patient>(`/person/${id}`, patient);
    return response.data!;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/person/${id}`);
  },

  async getForSelect(params?: { search?: string }): Promise<Patient[]> {
    return this.getAll(params);
  },

  extractPatientData(formData: PatientFormData): Patient {
    const patient: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      maritalStatus: formData.maritalStatus || 'SINGLE',
      religion: formData.religion || '',
      nationality: formData.nationality || 'Brasileiro',
      contacts: [],
      addresses: [],
      paymentMethod: formData.paymentMethod || 'CASH',
      paymentPeriod: formData.paymentPeriod || 'PER_CONSULTATION',
      consultationValue: formData.consultationValue ? parseFloat(formData.consultationValue) : 0,
      issuesInvoice: formData.issuesInvoice || false
    };

    // Only include birthDate if it's provided
    if (formData.birthDate && formData.birthDate.trim() !== '') {
      let birthDateISO = formData.birthDate;
      if (!birthDateISO.includes('T')) {
        birthDateISO = `${birthDateISO}T00:00:00Z`;
      }
      patient.birthDate = birthDateISO;
    }

    if (formData.email) {
      patient.contacts.push({ 
        type: 'EMAIL', 
        value: formData.email,
        primary: true 
      });
    }
    if (formData.phone) {
      patient.contacts.push({ 
        type: 'PHONE', 
        value: formData.phone,
        primary: !formData.email 
      });
    }

    if (formData.street || formData.city) {
      patient.addresses.push({
        street: formData.street || '',
        number: formData.number || '',
        complement: formData.complement || '',
        district: formData.district || '',
        city: formData.city || '',
        state: formData.state || '',
        country: 'Brasil',
        zipCode: formData.zipCode || '',
        type: 'HOME'
      });
    }

    return patient;
  },
  
  fillFormData(patient: Patient): PatientFormData {
    const formData: PatientFormData = {
      firstName: patient.firstName || '',
      lastName: patient.lastName || '',
      birthDate: patient.birthDate || '',
      maritalStatus: patient.maritalStatus || '',
      religion: patient.religion || '',
      nationality: patient.nationality || '',
      email: '',
      phone: '',
      street: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      zipCode: '',
      paymentMethod: patient.paymentMethod || '',
      paymentPeriod: patient.paymentPeriod || '',
      consultationValue: patient.consultationValue || '',
      issuesInvoice: patient.issuesInvoice || false
    };

    if (patient.contacts && patient.contacts.length > 0) {
      patient.contacts.forEach((contact) => {
        if (contact.type === 'EMAIL') {
          formData.email = contact.value;
        } else if (contact.type === 'PHONE' || contact.type === 'MOBILE') {
          formData.phone = contact.value;
        }
      });
    }

    if (patient.addresses && patient.addresses.length > 0) {
      const address = patient.addresses[0];
      formData.street = address.street || '';
      formData.number = address.number || '';
      formData.complement = address.complement || '';
      formData.district = address.district || '';
      formData.city = address.city || '';
      formData.state = address.state || '';
      formData.zipCode = address.zipCode || '';
    }

    return formData;
  },

  // Helpers para formatação
  formatters: {
    formatDateBR(dateString: string): string {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      } catch (e) {
        return '-';
      }
    },

    parseDateBR(dateStr: string): string {
      if (!dateStr) return '';
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    },

    getFullName(patient: Patient): string {
      return `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    },

    getEmail(patient: Patient): string {
      const emailContact = patient.contacts?.find(c => c.type === 'EMAIL');
      return emailContact?.value || '-';
    },

    getPhone(patient: Patient): string {
      const phoneContact = patient.contacts?.find(c => c.type === 'PHONE' || c.type === 'MOBILE');
      return phoneContact?.value || '-';
    }
  }
};

export default patientsService;
